import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, BarChart3, ArrowRight } from 'lucide-react';
const DEMOS = [{
  prompt: 'Forecast Q4 revenue and chart it',
  headers: ['Month', 'Users', 'MRR', 'Growth'],
  rows: [['Oct', '12,450', '$142,000', '+12%'], ['Nov', '14,100', '$168,500', '+18%'], ['Dec', '16,800', '$210,000', '+24%']],
  chartType: 'bar'
}, {
  prompt: 'Clean up messy email column',
  headers: ['Raw Input', 'Cleaned Email', 'Status', 'Domain'],
  rows: [['john.doe @ gmail ', 'john.doe@gmail.com', 'Valid', 'gmail.com'], ['jane_smith(at)corp.com', 'jane_smith@corp.com', 'Valid', 'corp.com'], ['invalid--email.com', 'N/A', 'Invalid', 'N/A']],
  chartType: 'none'
}, {
  prompt: 'Group by region and sum sales',
  headers: ['Region', 'Q1 Sales', 'Q2 Sales', 'Total'],
  rows: [['North America', '$450k', '$520k', '$970k'], ['Europe', '$320k', '$380k', '$700k'], ['Asia Pacific', '$210k', '$290k', '$500k']],
  chartType: 'pie'
}];
export function HeroDemo() {
  const [demoIndex, setDemoIndex] = useState(0);
  const [typedPrompt, setTypedPrompt] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showResults, setShowResults] = useState(false);
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const currentDemo = DEMOS[demoIndex];
    if (isTyping) {
      if (typedPrompt.length < currentDemo.prompt.length) {
        timeout = setTimeout(() => {
          setTypedPrompt(currentDemo.prompt.slice(0, typedPrompt.length + 1));
        }, 50);
      } else {
        timeout = setTimeout(() => {
          setIsTyping(false);
          setShowResults(true);
        }, 500);
      }
    } else {
      timeout = setTimeout(() => {
        setShowResults(false);
        setTypedPrompt('');
        setIsTyping(true);
        setDemoIndex(prev => (prev + 1) % DEMOS.length);
      }, 4000);
    }
    return () => clearTimeout(timeout);
  }, [typedPrompt, isTyping, demoIndex]);
  const currentDemo = DEMOS[demoIndex];
  return <div className="relative w-full max-w-4xl mx-auto mt-16" data-id="element-114">
      {/* Ambient Glow */}
      <div className="absolute inset-0 bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none" data-id="element-115" />

      <div className="relative bg-zinc-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col" data-id="element-116">
        {/* Top Bar */}
        <div className="h-12 border-b border-white/10 flex items-center px-4 gap-4 bg-zinc-900/50" data-id="element-117">
          <div className="flex gap-1.5" data-id="element-118">
            <div className="w-3 h-3 rounded-full bg-white/20" data-id="element-119" />
            <div className="w-3 h-3 rounded-full bg-white/20" data-id="element-120" />
            <div className="w-3 h-3 rounded-full bg-white/20" data-id="element-121" />
          </div>

          {/* AI Prompt Input */}
          <div className="flex-1 max-w-xl mx-auto bg-zinc-950 border border-white/10 rounded-md h-8 flex items-center px-3 gap-2" data-id="element-122">
            <Sparkles size={14} className="text-emerald-400" data-id="element-123" />
            <span className="font-mono text-xs text-zinc-300" data-id="element-124">
              {typedPrompt}
              <span className="animate-pulse inline-block w-1.5 h-3 bg-emerald-400 ml-0.5 align-middle" data-id="element-125" />
            </span>
          </div>
        </div>

        {/* Spreadsheet Area */}
        <div className="flex-1 flex min-h-[240px] bg-zinc-950" data-id="element-126">
          <div className="flex-1 flex flex-col border-r border-white/10" data-id="element-127">
            {/* Headers */}
            <div className="flex border-b border-white/10 bg-zinc-900/30" data-id="element-128">
              <div className="w-10 border-r border-white/10 flex items-center justify-center text-[10px] text-zinc-600 font-mono bg-zinc-900/50" data-id="element-129"></div>
              {currentDemo.headers.map((h, i) => <div key={i} className="flex-1 px-3 py-2 text-xs font-medium text-zinc-400 border-r border-white/10 last:border-r-0" data-id="element-130">
                  {h}
                </div>)}
            </div>

            {/* Rows */}
            <div className="flex-1 relative" data-id="element-131">
              <AnimatePresence mode="wait" data-id="element-132">
                {showResults && <motion.div key={demoIndex} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} className="absolute inset-0" data-id="element-133">
                    {currentDemo.rows.map((row, rowIndex) => <motion.div key={rowIndex} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: rowIndex * 0.15
                }} className="flex border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-id="element-134">
                        <div className="w-10 border-r border-white/10 flex items-center justify-center text-[10px] text-zinc-600 font-mono bg-zinc-900/20" data-id="element-135">
                          {rowIndex + 1}
                        </div>
                        {row.map((cell, cellIndex) => <div key={cellIndex} className="flex-1 px-3 py-2 text-sm text-zinc-300 font-mono border-r border-white/5 last:border-r-0 truncate" data-id="element-136">
                            {cell}
                          </div>)}
                      </motion.div>)}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Right Sidebar (Generated Board/Chart) */}
          <div className="w-64 bg-zinc-900/20 p-4 flex flex-col gap-4" data-id="element-137">
            <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-2" data-id="element-138">
              <BarChart3 size={14} data-id="element-139" /> Generated Board
            </div>

            <AnimatePresence mode="wait" data-id="element-140">
              {showResults && currentDemo.chartType !== 'none' && <motion.div key={`chart-${demoIndex}`} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} transition={{
              delay: 0.4
            }} className="flex-1 border border-white/10 rounded-lg bg-zinc-950 p-3 flex flex-col gap-3" data-id="element-141">
                  <div className="h-2 w-24 bg-zinc-800 rounded-full" data-id="element-142" />
                  <div className="flex-1 flex items-end gap-2 pt-4" data-id="element-143">
                    {currentDemo.chartType === 'bar' ? <>
                        <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: '40%'
                  }} className="flex-1 bg-emerald-500/20 border border-emerald-500/50 rounded-t-sm" data-id="element-144" />
                        <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: '60%'
                  }} className="flex-1 bg-emerald-500/40 border border-emerald-500/50 rounded-t-sm" data-id="element-145" />
                        <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: '100%'
                  }} className="flex-1 bg-emerald-500/80 border border-emerald-500/50 rounded-t-sm" data-id="element-146" />
                      </> : <div className="w-full h-full flex items-center justify-center" data-id="element-147">
                        <div className="w-24 h-24 rounded-full border-4 border-emerald-500/30 border-t-emerald-500" data-id="element-148" />
                      </div>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}